<script setup lang="ts">
import WarehouseSelect from '@/components/common/WarehouseSelect.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, useSlots, watch } from 'vue'
import { NButton, NDatePicker, NGrid, NGridItem, NInput, NSelect, NTreeSelect } from 'naive-ui'
import type { SelectOption, TreeSelectOption } from 'naive-ui'
import { ChevronDown } from 'lucide-vue-next'
import { defaultDateRangeShortcuts } from '@/utils/formatTime'
import type { DateRangeShortcuts } from '@/utils/formatTime'
import type {
  SearchFilterAction,
  SearchFilterActionButtonProps,
  SearchFilterActionPayload,
  SearchFilterFieldChangePayload,
  SearchFilterField,
  SearchFilterLayout,
  SearchFilterPrefixSelect,
} from './types'

type DatePickerTo = string | boolean | HTMLElement
type DateRangePickerType = 'daterange' | 'datetimerange'
type DateRangePickerProps = Record<string, unknown> & {
  to: DatePickerTo
  shortcuts: DateRangeShortcuts
  startPlaceholder: string
  endPlaceholder: string
  format: string
  valueFormat: string
}
const dateValueFormat = 'yyyy-MM-dd'
const dateTimeValueFormat = 'yyyy-MM-dd HH:mm:ss'

const props = withDefaults(
  defineProps<{
    data: SearchFilterField[]
    /** 统一传入 Naive Grid 列数与间距。 */
    layout?: SearchFilterLayout
    actions?: SearchFilterAction[]
    actionButtonProps?: SearchFilterActionButtonProps
    /** 下拉字段变更后是否立即触发查询，默认保持仓库页面行为。 */
    autoSubmitOnSelect?: boolean
    /** 清空筛选项后是否立即触发查询，默认保持仓库页面行为。 */
    autoSubmitOnClear?: boolean
    /** 日期确认后是否立即触发查询，默认保持既有通用组件行为。 */
    autoSubmitOnDateConfirm?: boolean
    /** 是否根据筛选栏自身宽度在 1 至 5 列间自适应。 */
    adaptiveColumns?: boolean
    /** 筛选项超过首屏数量时是否自动折叠。 */
    collapsible?: boolean
    /** 默认展示的高频筛选项数量。 */
    collapseThreshold?: number
  }>(),
  {
    actions: () => [],
    actionButtonProps: () => ({}),
    autoSubmitOnSelect: true,
    autoSubmitOnClear: true,
    autoSubmitOnDateConfirm: true,
    adaptiveColumns: true,
    collapsible: true,
    collapseThreshold: 4,
  },
)

const emit = defineEmits<{
  action: [payload: SearchFilterActionPayload]
  fieldChange: [payload: SearchFilterFieldChangePayload]
}>()

const slots = useSlots()
const barRef = ref<HTMLElement | null>(null)
const gridRef = ref<InstanceType<typeof NGrid> | null>(null)
const actionsRef = ref<HTMLElement | null>(null)
const gridWidth = ref(0)
const actionsWidth = ref(0)
let resizeObserver: ResizeObserver | undefined

onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === barRef.value) gridWidth.value = entry.contentRect.width
      if (entry.target === actionsRef.value) actionsWidth.value = entry.contentRect.width
    }
  })
  if (barRef.value) resizeObserver.observe(barRef.value)
  if (actionsRef.value) resizeObserver.observe(actionsRef.value)
})
onBeforeUnmount(() => resizeObserver?.disconnect())

const filterData = reactive<Record<string, unknown>>({})
const filtersExpanded = ref(false)
let isClearingFilter = false

/** 按页面传入的字段顺序展示筛选项，仅过滤显式隐藏的字段。 */
const visibleFields = computed(() => props.data.filter((item) => item.visible !== false))
const hasMoreFilters = computed(
  () => props.collapsible && visibleFields.value.length > Math.max(1, props.collapseThreshold),
)
/** 折叠和展开始终使用相同列数，隐藏字段不改变输入框宽度。 */
const defaultGridCols = '1 480:2 640:3 1060:4 1400:5'
const gridProps = computed(() => ({
  cols: props.layout?.cols ?? (props.adaptiveColumns ? defaultGridCols : '1 s:2 m:4'),
  responsive: props.layout?.responsive ?? (props.adaptiveColumns ? 'self' : 'screen'),
  xGap: props.layout?.xGap ?? 12,
  yGap: props.layout?.yGap ?? 12,
}))
const columnCount = computed(() => gridRef.value?.responsiveCols || 1)
const isCollapsed = computed(() => props.collapsible && !filtersExpanded.value)
/** 按按钮组实际宽度预留完整栅格列，不压缩字段来容纳操作。 */
const minimumActionSpan = computed(() => {
  const gap = Number.parseFloat(String(gridRef.value?.style.columnGap ?? 12)) || 0
  const columnWidth = (gridWidth.value + gap) / columnCount.value
  return Math.min(
    columnCount.value,
    Math.max(1, Math.ceil((actionsWidth.value + gap) / (columnWidth || 1))),
  )
})
/** 计算操作区可复用的剩余列；按钮组宽度不足时由自身换行，避免桌面端无故另起一行。 */
function actionSpanForColumns(fieldCount: number, columns: number) {
  const remainder = fieldCount % columns
  const remainingColumns = remainder === 0 ? columns : columns - remainder
  return remainingColumns
}
const defaultActionLayout = computed(() => {
  if (isCollapsed.value) {
    return { span: minimumActionSpan.value, suffix: true }
  }
  if (slots.btns || props.layout?.cols) {
    return { span: columnCount.value }
  }
  return {
    span: Math.max(
      minimumActionSpan.value,
      actionSpanForColumns(visibleFields.value.length, columnCount.value),
    ),
  }
})

const standardActions: Record<string, Pick<SearchFilterAction, 'label' | 'buttonProps'>> = {
  export: { label: '导出' },
}
/** 统一补齐标准动作的文案与样式，并透传页面控制的禁用和加载状态。 */
function normalizeAction(action: SearchFilterAction): SearchFilterAction {
  const standardAction = standardActions[action.type]
  return {
    ...standardAction,
    ...action,
    label: action.label ?? standardAction?.label ?? action.type,
    buttonProps: {
      ...(standardAction?.buttonProps || {}),
      ...(action.loading === undefined ? {} : { loading: action.loading }),
      ...(action.disabled === undefined ? {} : { disabled: action.disabled }),
      ...(action.buttonProps || {}),
    },
  }
}
const visibleActions = computed<SearchFilterAction[]>(() =>
  [
    {
      label: '查询',
      type: 'search',
      buttonProps: { type: 'primary', ...(props.actionButtonProps.search || {}) },
    },
    { label: '重置', type: 'reset', buttonProps: props.actionButtonProps.reset },
    ...props.actions,
  ]
    .filter((item) => item.visible !== false)
    .map(normalizeAction),
)

function dateLabelPrefix(item: SearchFilterField) {
  const rawLabel =
    typeof item.label === 'string' && item.label.trim()
      ? item.label
      : typeof item.props?.placeholder === 'string'
        ? item.props.placeholder
        : ''
  const label = rawLabel
    .trim()
    .replace(/^(请选择|请输入|全部)/, '')
    .trim()
  if (!label) return ''
  const labelWithoutRange = label.endsWith('范围') ? label.slice(0, -2).trim() : label
  return labelWithoutRange.replace(/(日期|时间)$/, '').trim() || labelWithoutRange
}

function normalizeDateTimeString(value: unknown, boundary: 'start' | 'end') {
  if (typeof value !== 'string') return value
  const trimmedValue = value.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) return trimmedValue
  return `${trimmedValue} ${boundary === 'start' ? '00:00:00' : '23:59:59'}`
}

function normalizeDateRangeValue(item: SearchFilterField, value: unknown) {
  if (item.type !== 'date.string' || !Array.isArray(value) || !item.enableTime) return value
  return [normalizeDateTimeString(value[0], 'start'), normalizeDateTimeString(value[1], 'end')]
}

function datePickerTo(value: unknown): DatePickerTo {
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) return value
  return false
}

function dateRangeShortcuts(value: unknown): DateRangeShortcuts {
  if (value && typeof value === 'object') return value as DateRangeShortcuts
  return defaultDateRangeShortcuts
}

function stringProp(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback
}

function dateRangePickerType(item: SearchFilterField): DateRangePickerType {
  return item.enableTime ? 'datetimerange' : 'daterange'
}

function dateRangePickerProps(item: SearchFilterField): DateRangePickerProps {
  const prefix = dateLabelPrefix(item)
  const fieldProps = item.props || {}
  const valueFormat = item.enableTime ? dateTimeValueFormat : dateValueFormat
  return {
    ...fieldProps,
    to: datePickerTo(fieldProps.to),
    shortcuts: dateRangeShortcuts(fieldProps.shortcuts),
    startPlaceholder: stringProp(fieldProps.startPlaceholder, `${prefix}开始日期`),
    endPlaceholder: stringProp(fieldProps.endPlaceholder, `${prefix}结束日期`),
    format: valueFormat,
    valueFormat,
  }
}

function optionValue(option: SelectOption | TreeSelectOption) {
  const record = option as Record<string, unknown>
  return record.value ?? record.key
}

function isAllOption(option: SelectOption | TreeSelectOption) {
  const label = (option as Record<string, unknown>).label
  const value = optionValue(option)
  if (value === 'all') return true
  if (typeof label !== 'string' || !label.trim().startsWith('全部')) return false
  return value === '' || value === null || value === undefined || value === 0
}

function filterAllOptions(
  options: SearchFilterField['options'] = [],
): Array<SelectOption | TreeSelectOption> {
  return options
    .filter((option) => !isAllOption(option))
    .map((option) => {
      const children = (option as Record<string, unknown>).children
      if (!Array.isArray(children)) return option
      return {
        ...option,
        children: filterAllOptions(children as Array<SelectOption | TreeSelectOption>),
      }
    })
}

function hasAllValue(item: SearchFilterField, value: unknown) {
  if (Array.isArray(value)) return false
  return (item.options || []).some(
    (option) => Object.is(optionValue(option), value) && isAllOption(option),
  )
}

function isChoiceField(item: SearchFilterField) {
  return item.type === 'select' || item.type === 'select.warehouse' || item.type === 'tree-select'
}

function normalizeFieldValue(item: SearchFilterField, value: unknown) {
  if (isChoiceField(item) && (value === '' || value === undefined || hasAllValue(item, value))) {
    return null
  }
  const normalizedValue = normalizeDateRangeValue(item, value)
  return Array.isArray(normalizedValue) ? [...normalizedValue] : normalizedValue
}

function normalizePlaceholder(item: SearchFilterField) {
  const fieldProps = item.props || {}
  const placeholder = fieldProps.placeholder
  if (typeof placeholder !== 'string' || !placeholder.trim().startsWith('全部')) {
    return fieldProps
  }
  const suffix =
    placeholder.trim() === '全部' || placeholder.includes('/')
      ? String(item.label || '').trim()
      : placeholder.trim().replace(/^全部/, '').trim()
  return {
    ...fieldProps,
    placeholder: suffix ? `请选择${suffix}` : '请选择',
  }
}

function inputProps(item: SearchFilterField) {
  const fieldProps = item.props || {}
  const configuredPlaceholder = fieldProps.placeholder
  return {
    ...fieldProps,
    placeholder:
      typeof configuredPlaceholder === 'string' && configuredPlaceholder.trim()
        ? configuredPlaceholder
        : `请输入${item.label || '筛选条件'}`,
  }
}

function prefixSelectProps(prefixSelect: SearchFilterPrefixSelect) {
  const fieldProps = prefixSelect.props || {}
  const configuredPlaceholder = fieldProps.placeholder
  return {
    clearable: true,
    ...fieldProps,
    placeholder:
      typeof configuredPlaceholder === 'string' && configuredPlaceholder.trim()
        ? configuredPlaceholder
        : `请选择${prefixSelect.label || '日期类型'}`,
  }
}

function prefixSelectStyle(prefixSelect: SearchFilterPrefixSelect) {
  const configuredWidth = prefixSelect.width
  const width =
    typeof configuredWidth === 'number'
      ? `${configuredWidth}px`
      : typeof configuredWidth === 'string' && configuredWidth.trim()
        ? configuredWidth
        : '120px'
  return { '--search-filter-prefix-width': width }
}

function normalizePrefixSelectValue(prefixSelect: SearchFilterPrefixSelect) {
  const value = prefixSelect.value
  if (value === '' || value === undefined) return null
  return Array.isArray(value) ? [...value] : value
}

/** 初始化或重置筛选条件，保持页面传入的后端字段名。 */
function resetFormData() {
  props.data.forEach((item) => {
    filterData[item.field] = normalizeFieldValue(item, item.value)
    if (item.prefixSelect) {
      filterData[item.prefixSelect.field] = normalizePrefixSelectValue(item.prefixSelect)
    }
  })
}

/** 统一向页面派发查询栏操作，不为不同按钮拆分事件。 */
function handleAction(action: SearchFilterAction) {
  if (action.type === 'reset') {
    resetFormData()
  }
  emit('action', { type: action.type, data: { ...filterData } })
}

/** 同步筛选字段值，并通知页面更新对应查询参数。 */
function updateFieldValue(field: string, value: unknown) {
  filterData[field] = value
  emit('fieldChange', { field, value })
}

function dayPickerValue(field: string): number | null {
  return filterData[field] as number | null
}

function dateRangeValue(field: string): [number, number] | null {
  return filterData[field] as [number, number] | null
}

function formattedDateRangeValue(field: string): [string, string] | null {
  return filterData[field] as [string, string] | null
}

function resolveDateConfirmValue(item: SearchFilterField, value: unknown, formattedValue: unknown) {
  if (item.type === 'day.string' || item.type === 'date.string') {
    return formattedValue !== undefined ? formattedValue : value
  }
  return value
}

/** 处理选择类字段变更，并按配置决定是否立即查询。 */
function handleSelectValueChange(field: string, value: unknown) {
  updateFieldValue(field, value)
  if (isClearingFilter) return
  if (!props.autoSubmitOnSelect) return
  handleAction(visibleActions.value[0])
}

/** 处理树选择器变更，并区分选择和清空各自的自动查询开关。 */
function handleTreeSelectValueChange(field: string, value: unknown, meta?: { action?: string }) {
  updateFieldValue(field, value)
  if (meta?.action === 'clear') {
    if (props.autoSubmitOnClear) {
      handleAction(visibleActions.value[0])
    }
    return
  }
  if (!props.autoSubmitOnSelect) return
  handleAction(visibleActions.value[0])
}

/** 清空筛选项后等待字段值同步，再通知页面按新条件查询。 */
function handleFilterClear() {
  isClearingFilter = true
  void nextTick(() => {
    if (props.autoSubmitOnClear) {
      handleAction(visibleActions.value[0])
    }
    isClearingFilter = false
  })
}

/** 日期选择确认后复用查询操作，避免用户再次点击查询按钮。 */
function handleDateConfirm(item: SearchFilterField, value?: unknown, formattedValue?: unknown) {
  const confirmedValue = resolveDateConfirmValue(item, value, formattedValue)
  if (confirmedValue !== undefined) {
    updateFieldValue(item.field, confirmedValue)
  }
  if (!props.autoSubmitOnDateConfirm) return
  handleAction(visibleActions.value[0])
}

/** 生成字段值快照，选项刷新时不重置用户已输入的筛选条件。 */
const fieldValueSignature = computed(() =>
  props.data
    .map((item) => {
      const prefixSelectSignature = item.prefixSelect
        ? `${item.prefixSelect.field}:${JSON.stringify(item.prefixSelect.value)}`
        : ''
      return `${item.field}:${JSON.stringify(item.value)}:${prefixSelectSignature}`
    })
    .join('|'),
)

watch(fieldValueSignature, () => resetFormData(), { immediate: true })
</script>

<template>
  <div
    ref="barRef"
    class="search-filter-bar mb-4"
    role="search"
  >
    <NGrid
      ref="gridRef"
      v-bind="gridProps"
      :collapsed="isCollapsed"
      :collapsed-rows="minimumActionSpan >= columnCount ? 2 : 1"
    >
      <NGridItem
        v-for="(item, index) in visibleFields"
        :key="item.field"
        v-bind="item.layout"
        class="search-filter-bar__field"
        :class="{
          'search-filter-bar__field--hidden':
            isCollapsed && index >= Math.max(1, collapseThreshold),
        }"
      >
        <slot
          :name="item.field"
          :field="item"
          :value="filterData[item.field]"
          :update-value="(value: unknown) => updateFieldValue(item.field, value)"
          :date-range-shortcuts="defaultDateRangeShortcuts"
        >
          <NInput
            v-if="item.type === 'input.text'"
            :value="String(filterData[item.field] ?? '')"
            :aria-label="item.label || item.props?.placeholder"
            clearable
            size="large"
            v-bind="inputProps(item)"
            @update:value="updateFieldValue(item.field, $event)"
            @clear="handleFilterClear"
            @keyup.enter="handleAction(visibleActions[0])"
          />
          <component
            :is="item.type === 'select.warehouse' ? WarehouseSelect : NSelect"
            v-else-if="item.type === 'select' || item.type === 'select.warehouse'"
            :value="filterData[item.field] as never"
            :aria-label="item.label || item.props?.placeholder"
            clearable
            size="large"
            :options="filterAllOptions(item.options)"
            v-bind="normalizePlaceholder(item)"
            @update:value="handleSelectValueChange(item.field, $event)"
            @clear="handleFilterClear"
          />
          <NTreeSelect
            v-else-if="item.type === 'tree-select'"
            :value="filterData[item.field] as never"
            :aria-label="item.label || item.props?.placeholder"
            clearable
            size="large"
            :options="filterAllOptions(item.options) as never"
            v-bind="normalizePlaceholder(item)"
            @update:value="
              (value, _option, meta) => handleTreeSelectValueChange(item.field, value, meta)
            "
          />
          <NDatePicker
            v-else-if="item.type === 'day'"
            :value="dayPickerValue(item.field)"
            :aria-label="item.label || item.props?.placeholder"
            type="date"
            size="large"
            clearable
            v-bind="item.props"
            @update:value="updateFieldValue(item.field, $event)"
            @clear="handleFilterClear"
            @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
          />
          <NDatePicker
            v-else-if="item.type === 'day.string'"
            :formatted-value="filterData[item.field] ? String(filterData[item.field]) : null"
            :aria-label="item.label || item.props?.placeholder"
            type="date"
            size="large"
            clearable
            v-bind="item.props"
            @update:formatted-value="updateFieldValue(item.field, $event)"
            @clear="handleFilterClear"
            @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
          />
          <div
            v-else-if="(item.type === 'date' || item.type === 'date.string') && item.prefixSelect"
            class="search-filter-bar__date-combo"
            :style="prefixSelectStyle(item.prefixSelect)"
          >
            <NSelect
              :value="filterData[item.prefixSelect.field] as never"
              :aria-label="item.prefixSelect.label || item.prefixSelect.props?.placeholder"
              class="search-filter-bar__date-combo-select"
              size="large"
              :options="filterAllOptions(item.prefixSelect.options)"
              v-bind="prefixSelectProps(item.prefixSelect)"
              @update:value="handleSelectValueChange(item.prefixSelect.field, $event)"
              @clear="handleFilterClear"
            />
            <NDatePicker
              v-if="item.type === 'date'"
              :value="dateRangeValue(item.field)"
              :aria-label="item.label || item.props?.placeholder"
              :type="dateRangePickerType(item)"
              class="search-filter-bar__date-combo-picker"
              size="large"
              clearable
              v-bind="dateRangePickerProps(item)"
              @update:value="updateFieldValue(item.field, $event)"
              @clear="handleFilterClear"
              @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
            />
            <NDatePicker
              v-else
              :formatted-value="formattedDateRangeValue(item.field)"
              :aria-label="item.label || item.props?.placeholder"
              :type="dateRangePickerType(item)"
              class="search-filter-bar__date-combo-picker"
              size="large"
              clearable
              v-bind="dateRangePickerProps(item)"
              @update:formatted-value="updateFieldValue(item.field, $event)"
              @clear="handleFilterClear"
              @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
            />
          </div>
          <NDatePicker
            v-else-if="item.type === 'date'"
            :value="dateRangeValue(item.field)"
            :aria-label="item.label || item.props?.placeholder"
            :type="dateRangePickerType(item)"
            size="large"
            clearable
            v-bind="dateRangePickerProps(item)"
            @update:value="updateFieldValue(item.field, $event)"
            @clear="handleFilterClear"
            @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
          />
          <NDatePicker
            v-else-if="item.type === 'date.string'"
            :formatted-value="formattedDateRangeValue(item.field)"
            :aria-label="item.label || item.props?.placeholder"
            :type="dateRangePickerType(item)"
            size="large"
            clearable
            v-bind="dateRangePickerProps(item)"
            @update:formatted-value="updateFieldValue(item.field, $event)"
            @clear="handleFilterClear"
            @confirm="(value, formattedValue) => handleDateConfirm(item, value, formattedValue)"
          />
        </slot>
      </NGridItem>
      <!-- suffix 负责折叠占位，操作区仍按字段顺序紧邻排列。 -->
      <NGridItem
        v-slot="{ overflow }"
        v-bind="defaultActionLayout"
        :style="{ gridColumnStart: 'auto' }"
        class="search-filter-bar__action-item"
      >
        <div
          ref="actionsRef"
          class="search-filter-bar__actions"
        >
          <div class="search-filter-bar__button-group">
            <NButton
              v-for="action in visibleActions"
              :key="action.type"
              v-bind="action.buttonProps"
              size="large"
              @click="handleAction(action)"
            >
              {{ action.label }}
            </NButton>
            <slot
              name="actions"
              :data="filterData"
            />
            <slot
              name="btns"
              :data="filterData"
            />
          </div>
          <div
            v-if="collapsible && (hasMoreFilters || overflow || filtersExpanded)"
            class="search-filter-bar__more-group"
          >
            <NButton
              quaternary
              attr-type="button"
              class="search-filter-bar__more"
              :aria-expanded="filtersExpanded"
              @click="filtersExpanded = !filtersExpanded"
            >
              <template #icon>
                <ChevronDown
                  :size="16"
                  class="search-filter-bar__more-icon"
                  :class="{ 'search-filter-bar__more-icon--expanded': filtersExpanded }"
                />
              </template>
              {{ filtersExpanded ? '收起' : '展开' }}
            </NButton>
          </div>
        </div>
      </NGridItem>
    </NGrid>
  </div>
</template>

<style scoped>
.search-filter-bar {
  display: grid;
  gap: 12px;
  padding: 24px 24px;
  border-radius: 8px;
  background: #fff;
  container-type: inline-size;
}

.search-filter-bar__field {
  min-width: 0;
}

.search-filter-bar__field--hidden {
  display: none !important;
}

.search-filter-bar__date-combo {
  display: grid;
  min-width: 0;
  grid-template-columns: var(--search-filter-prefix-width) minmax(0, 1fr);
}

.search-filter-bar__date-combo-select,
.search-filter-bar__date-combo-picker {
  min-width: 0;
}

.search-filter-bar__date-combo-select {
  z-index: 1;
  --n-border-radius: 4px 0 0 4px !important;
}

.search-filter-bar__date-combo-picker {
  margin-left: -1px;
  --n-border-radius: 0 4px 4px 0 !important;
}

.search-filter-bar__date-combo-select:focus-within,
.search-filter-bar__date-combo-picker:focus-within {
  z-index: 2;
}

.search-filter-bar__date-combo-select :deep(.n-base-selection),
.search-filter-bar__date-combo-select :deep(.n-base-selection-label) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.search-filter-bar__date-combo-picker :deep(.n-input),
.search-filter-bar__date-combo-picker :deep(.n-input-wrapper) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.search-filter-bar__action-item {
  min-width: 0;
}

.search-filter-bar__actions {
  display: flex;
  width: max-content;
  min-width: 0;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px 12px;
}

.search-filter-bar__button-group {
  display: flex;
  max-width: 100%;
  min-width: 0;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  white-space: nowrap;
}

.search-filter-bar__more-group {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 auto;
}

.search-filter-bar__more {
  flex: 0 0 auto;
}

.search-filter-bar__more-icon {
  transition: transform 0.2s ease;
}

.search-filter-bar__more-icon--expanded {
  transform: rotate(180deg);
}

.search-filter-bar :deep(.n-date-panel--daterange),
.search-filter-bar :deep(.n-date-panel--datetimerange),
:global(.n-date-panel--daterange),
:global(.n-date-panel--datetimerange) {
  position: relative;
  z-index: 20;
  overflow: hidden;
  padding-left: 120px;
}

.search-filter-bar :deep(.n-date-panel--daterange .n-date-panel-actions__prefix),
.search-filter-bar :deep(.n-date-panel--datetimerange .n-date-panel-actions__prefix),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix),
:global(.n-date-panel--datetimerange .n-date-panel-actions__prefix) {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  width: 120px;
  margin-bottom: 0;
  padding: 9px 14px;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  border-right: 1px solid var(--n-panel-action-divider-color);
  border-top-left-radius: var(--n-panel-border-radius);
  border-bottom-left-radius: var(--n-panel-border-radius);
  background: var(--n-panel-color);
  box-sizing: border-box;
}

.search-filter-bar :deep(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button),
.search-filter-bar :deep(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button),
:global(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button) {
  width: 100%;
  height: 32px;
  margin: 0;
  padding: 0;
  justify-content: flex-start;
  --n-border: 0 solid transparent !important;
  --n-border-hover: 0 solid transparent !important;
  --n-border-pressed: 0 solid transparent !important;
  --n-border-focus: 0 solid transparent !important;
  --n-color: transparent !important;
  --n-color-hover: #f5f7fa !important;
  --n-color-pressed: #eef3ff !important;
  --n-color-focus: #f5f7fa !important;
  --n-ripple-color: transparent !important;
  --n-text-color: #1f2329 !important;
  --n-text-color-hover: #1f2329 !important;
  --n-text-color-pressed: #1f2329 !important;
  --n-text-color-focus: #1f2329 !important;
  font-size: 14px;
  font-weight: 400;
  line-height: 32px;
}

.search-filter-bar
  :deep(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button:not(:last-child)),
.search-filter-bar
  :deep(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button:not(:last-child)),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button:not(:last-child)),
:global(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button:not(:last-child)) {
  margin-right: 0;
}

.search-filter-bar
  :deep(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__content),
.search-filter-bar
  :deep(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__content),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__content),
:global(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__content) {
  justify-content: flex-start;
  padding-left: 14px;
}

.search-filter-bar
  :deep(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__border),
.search-filter-bar
  :deep(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__state-border),
.search-filter-bar
  :deep(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__border),
.search-filter-bar
  :deep(
    .n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__state-border
  ),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__border),
:global(.n-date-panel--daterange .n-date-panel-actions__prefix .n-button .n-button__state-border),
:global(.n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__border),
:global(
  .n-date-panel--datetimerange .n-date-panel-actions__prefix .n-button .n-button__state-border
) {
  display: none;
}

/* .search-filter-bar :deep(.n-input),
.search-filter-bar :deep(.n-base-selection) {
  min-height: 40px;
} */

@container (max-width: 560px) {
  .search-filter-bar__actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .search-filter-bar__button-group {
    flex-wrap: wrap;
    flex: 0 1 auto;
    white-space: normal;
  }
}
</style>
